// src/screens/HotelBookings.jsx
import useIdleLogout from "../hooks/useIdleLogout";
import "../css/HotelBookings.css";
import "bootstrap-icons/font/bootstrap-icons.css";

import { useMemo, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
ChartJS.register(ArcElement, Tooltip, Legend);

const PALETTE = [
  "#0d6efd", "#198754", "#dc3545", "#ffc107", "#20c997",
  "#6f42c1", "#fd7e14", "#0dcaf0", "#6610f2", "#1982c4"
];

const unique = (list) => Array.from(new Set(list.filter(Boolean)));

export default function HotelBookings() {
  useIdleLogout();

  // later replace with API data
  const [bookings] = useState([]);

  /* ========================= METRICS ========================= */
  const totalBookings = bookings.length;

  const inProgress = useMemo(() => {
    const set = new Set(["pending", "confirmed", "processing", "booked"]);
    return bookings.filter((b) => set.has(String(b.status || "").toLowerCase())).length;
  }, [bookings]);

  const checkedIn = useMemo(() => {
    const set = new Set(["checked_in", "checked-in", "checked in"]);
    return bookings.filter((b) => set.has(String(b.status || "").toLowerCase())).length;
  }, [bookings]);

  const checkedOut = useMemo(() => {
    const set = new Set(["checked_out", "checked-out", "checked out", "completed"]);
    return bookings.filter((b) => set.has(String(b.status || "").toLowerCase())).length;
  }, [bookings]);

  /* ========================= CHARTS ========================= */
  // Chart 1: Hotel Names
  const hotelLabels = useMemo(() => {
    const labels = unique(bookings.map((b) => (b.hotel || b.hotel_name || "").toString().trim()));
    return labels.length ? labels : ["No Data"];
  }, [bookings]);

  const hotelDonut = useMemo(() => {
    const labels = hotelLabels;
    const data =
      labels[0] === "No Data"
        ? [1]
        : labels.map((lbl) =>
          bookings.filter(
            (b) => ((b.hotel || b.hotel_name || "").toString().trim() || "").toLowerCase() === lbl.toLowerCase()
          ).length
        );

    return {
      labels,
      datasets: [{ data, backgroundColor: labels.map((_, i) => PALETTE[i % PALETTE.length]) }],
    };
  }, [hotelLabels, bookings]);

  // Chart 2: Payment Method Breakdown (Checked-Out Only)
  const checkedOutRows = useMemo(() => {
    const done = new Set(["checked_out", "checked-out", "checked out", "completed"]);
    return bookings.filter((b) => done.has(String(b.status || "").toLowerCase()));
  }, [bookings]);

  const paymentLabels = useMemo(() => {
    const labels = unique(
      checkedOutRows.map((b) => (b.payment || b.payment_method || "").toString().trim() || "Unknown")
    );
    return labels.length ? labels : ["No Data"];
  }, [checkedOutRows]);

  const paymentDonut = useMemo(() => {
    const labels = paymentLabels;
    const data =
      labels[0] === "No Data"
        ? [1]
        : labels.map((p) =>
          checkedOutRows.filter(
            (b) => ((b.payment || b.payment_method || "Unknown").toString().trim() || "Unknown") === p
          ).length
        );

    return {
      labels,
      datasets: [{ data, backgroundColor: labels.map((_, i) => PALETTE[(i + 3) % PALETTE.length]) }],
    };
  }, [paymentLabels, checkedOutRows]);

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { position: "bottom", labels: { boxWidth: 12, padding: 10, font: { size: 11 } } },
      tooltip: { callbacks: { label: (ctx) => `${ctx.label || ""}: ${ctx.raw ?? 0}` } },
    },
  };

  /* ========================= EXPORT PLACEHOLDERS ========================= */
  const handleExportExcel = () => alert("Export Excel (hook later)");
  const handleExportPDF = () => alert("Export PDF (hook later)");

  return (
    <div className="container-fluid px-4 hotel-bookings-page">
      <section className="mb-5">
        {/* ✅ Metrics */}
        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-3 mb-4 hotel-metrics">
          {[
            { label: "Total Bookings", value: totalBookings },
            { label: "In Progress", value: inProgress },
            { label: "Checked-In", value: checkedIn },
            { label: "Checked-Out", value: checkedOut },
          ].map((m, i) => (
            <div className="col" key={i}>
              <div className="card metrics-card h-100">
                <div className="card-body">
                  <h6 className="text-secondary mb-1">{m.label}</h6>
                  <div className="fs-2 fw-bold">{m.value}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ✅ Charts */}
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <div className="card chart-card h-100">
              <div className="card-header">Hotel Names</div>
              <div className="card-body">
                <Doughnut data={hotelDonut} options={doughnutOptions} />
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card chart-card h-100">
              <div className="card-header">Payment Method Breakdown (Checked-Out Only)</div>
              <div className="card-body">
                <Doughnut data={paymentDonut} options={doughnutOptions} />
              </div>
            </div>
          </div>
        </div>

        {/* ✅ Toolbar (matches your 2nd image style) */}
        <div className="card mb-4 search-filter-card">
          <div className="card-body searchbar-row-inline">
            <span className="search-icon" aria-hidden="true">
              <i className="bi bi-search" />
            </span>

            <div className="search-wrap">
              <input
                type="text"
                className="form-control search-pill"
                placeholder="Search by booking id, guest, hotel, phone..."
                aria-label="Search hotel bookings"
              />
            </div>

            <div className="date-range">
              <input type="date" className="date-pill" aria-label="Check-in from" />
              <span className="range-sep">to</span>
              <input type="date" className="date-pill" aria-label="Check-in to" />
            </div>

            <div className="exports">
              <button type="button" className="btn btn-success" onClick={handleExportExcel}>
                Export Excel
              </button>
              <button type="button" className="btn btn-danger" onClick={handleExportPDF}>
                Export PDF
              </button>
            </div>
          </div>
        </div>

        {/* ✅ Table (fixed column spacing) */}
        <div className="card hotel-table-card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Hotel Bookings List</h5>
          </div>

          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0 align-middle table-pro">
                <colgroup>
                  <col style={{ width: "80px" }} />
                  <col style={{ width: "170px" }} />
                  <col style={{ width: "200px" }} />
                  <col style={{ width: "160px" }} />
                  <col style={{ width: "220px" }} />
                  <col style={{ width: "170px" }} />
                  <col style={{ width: "140px" }} />
                  <col style={{ width: "140px" }} />
                  <col style={{ width: "110px" }} />
                  <col style={{ width: "150px" }} />
                  <col style={{ width: "140px" }} />
                  <col style={{ width: "140px" }} />
                </colgroup>

                <thead>
                  <tr>
                    <th>SI.NO</th>
                    <th>Booking ID</th>
                    <th>Guest Name</th>
                    <th>Phone</th>
                    <th>Hotel</th>
                    <th>Room Type</th>
                    <th>Check-in</th>
                    <th>Check-out</th>
                    <th>Nights</th>
                    <th>Amount (Nu)</th>
                    <th>Payment</th>
                    <th>Status</th>
                  </tr>
                </thead>

                <tbody>
                  <tr>
                    <td colSpan="12" className="text-center py-4 text-muted">
                      No hotel bookings found yet.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <footer className="dashboard-footer">
        <small>
          © {new Date().getFullYear()} <strong>སྟབས་བདེ། </strong> Admin • All rights reserved
        </small>
      </footer>
    </div>
  );
}
